import { Button, TextArea, TextInput } from '@wfp/react';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  commentsErrorsMessages,
  maxCommentTitleCharacters,
  SUCCESS_REQUEST,
  UPDATE,
} from 'src/constants';
import { requestAPI, requestAPIHoldingData } from 'src/redux/api/actions';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import { COMMENTS_LIST, REPORT_DETAIL_DATA_KEY } from 'src/redux/constants';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import { sendGAEvent } from 'src/utils';

interface FormErrors {
  content?: string;
  title?: string;
}

interface Props {
  sectionAPIKwargs?: any;
  commentsContainerID?: string;
  draftComment?: any;
  toggleFormDisplay?: () => void;
}

export const CommentForm = ({
  sectionAPIKwargs,
  commentsContainerID,
  draftComment,
  toggleFormDisplay,
}: Props) => {
  const [title, setTitle] = useState(draftComment?.title || '');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [content, setContent] = useState('');
  const [anchor] = useState<{ html_id?: string }>({});

  const dispatch = useDispatch();

  useEffect(() => {
    if (draftComment?.title) setTitle(draftComment.title);
  }, [draftComment?.title]);

  const resetFields = () => {
    setTitle('');
    setContent('');
  };

  const formErrors = useMemo(() => {
    const errors: FormErrors = {};

    if (content.trim() === '') {
      errors.content = 'empty';
    }

    if (title.trim() === '') {
      errors.title = 'empty';
    } else if (title.length > maxCommentTitleCharacters) {
      errors.title = 'maxLength';
    }

    return errors;
  }, [title, content]);

  const createComment = () => {
    resetFields();

    if (draftComment) {
      dispatch(
        changeViewStateAction({
          draftComment: {
            title,
            content,
            anchorID: draftComment?.anchorID,
            selection: draftComment?.selection,
            isCompleted: true,
            isExtra: draftComment?.isExtra,
            narrativeId: draftComment?.narrativeId,
          },
        }),
      );

      return;
    }

    toggleFormDisplay();

    const requestData = {
      content,
      title,
      anchor: anchor.html_id,
    };

    const params = {
      url_format: {
        ...sectionAPIKwargs,
      },
      method: 'post',
      data: requestData,
      storage_key: `${COMMENTS_LIST}_created`,
      success_actions: [
        sendSignalsAction({
          [commentsContainerID]: {
            [UPDATE]: true,
            [SUCCESS_REQUEST]: true,
          },
        }),
        requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
          url_format: { _id_: sectionAPIKwargs?._report_ },
        }),
      ],
    };

    dispatch(requestAPI(COMMENTS_LIST, params));
  };

  return (
    <div className="acr-comment-form">
      <div className="acr-comment-form-header">
        <div className="acr-comment-form-title">New comment</div>
        <button
          className="acr-comment-form-close"
          onClick={() => {
            if (draftComment) {
              dispatch(changeViewStateAction({ draftComment: undefined }));
              return;
            }
            toggleFormDisplay();
          }}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="acr-comment-form-inner">
        <TextInput
          className="acr-comment-title rounded-border"
          placeholder="Title here ..."
          id="comment-title"
          light={true}
          onChange={e => setTitle(e.target.value)}
          value={title}
          labelText=""
          invalid={formErrors?.title && isSubmitted}
          invalidText={commentsErrorsMessages['title']?.[formErrors?.title]}
        />
        <TextArea
          className="rounded-border acr-comment-body"
          placeholder="Comment here ..."
          data-test-id="comment-message-field"
          id="comment-msg-body"
          onChange={e => setContent(e.target.value)}
          value={content}
          labelText=""
          {...{ fullwidth: 'true' }}
          invalid={formErrors?.content && isSubmitted}
          invalidText={commentsErrorsMessages['content']?.[formErrors?.content]}
        />
        <div className="acr-comment-post-btn">
          <Button
            onClick={() => {
              if (draftComment) {
                dispatch(changeViewStateAction({ draftComment: undefined }));
                return;
              }
              toggleFormDisplay();
            }}
            data-test-id="cancel-comment-button"
            kind="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!isEmpty(formErrors)) {
                setIsSubmitted(true);
                return;
              }

              if (draftComment) {
                sendGAEvent('post_new_comment_from_narrative_btn_clicked');
              } else {
                sendGAEvent('post_new_comment_from_sidebar_btn_clicked');
              }

              createComment();
            }}
            data-test-id="post-new-comment-button"
          >
            Post Comment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommentForm;
